import React, { useCallback, useContext, useEffect, useState } from 'react';
import { LoggedInUserContext } from '../AdminPageContainer';
import { Box, FormControl, List, ListItem, ListItemButton, ListItemIcon, ListItemText, MenuItem, Select, Typography, Collapse } from '@mui/material';
import { GroupCompetition, Group as GroupType, ScenarioHeader } from 'src/Constants/Types';
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { MenuBook, School, Ballot } from '@mui/icons-material';
import { getGroups, getGroupsPerOrganization, updateUserLastGroupSelectionId, getScenarioHeaders, getSessionWithSessionId, getUser } from 'src/Services/BorgAPIClient';
import { Roles } from 'src/Constants/BorgEnums';
import { colorAuriaYellow } from 'src/GlobalStyles/MuiTheme';

const kepleraCourseID = "98823c64-6538-4063-a2f9-62146b9e6395";
const borgCourseID = "3757a0e0-01bc-4ccc-a3ed-e37e8118e416";
const isKepleraVersion = process.env.REACT_APP_PRODUCT_NAME === "keplera";

const COURSE_ID = isKepleraVersion ? kepleraCourseID : borgCourseID;

const INSTRUCTOR_MENU_ITEMS: any[] = [
  { title: 'Courses', icon: <School />, path: '/instructor/gradebook/:sessionId', menuItemType: "dropDown" },
  { title: 'Gradebook', icon: <MenuBook />, path: '/instructor/gradebook' },
  { title: 'View All Courses', icon: <School />, path: '/instructor/courses' },
];

const InstructorSidebar: React.FC = () => {
  const loggedInUser = useContext(LoggedInUserContext)?.loggedInUser;   // Logged in User data (used for filtering pages)
  const selectedPage = useContext(LoggedInUserContext)?.selectedPage;   // Selected page
  const adminGroupList = useContext(LoggedInUserContext)?.adminGroupList;   // Used for updating the group list drop down after creating a new group in Groups Admin Page
  const setQuizContext = useContext(LoggedInUserContext)?.setQuizContext;
  const currentQuizId = useContext(LoggedInUserContext)?.currentQuizId;

  const navigate = useNavigate();
  const location = useLocation();

  const [currentGroupList, setCurrentGroupList] = useState<GroupType[]>([]); //Used for the course drop down
  const [selectedCourseId, setSelectedCourseId] = useState<string>('');
  const [selectedGroupData, setSelectedGroupData] = useState<GroupType | null>(null); //Used to show Join ID
  const [selectedPath, setSelectedPath] = useState<string>(location.pathname.split('/')[2] || ""); // Initialize based on the current location
  const [quizzes, setQuizzes] = useState<ScenarioHeader[]>([]);
  const [selectedQuizId, setSelectedQuizId] = useState<string>('');

  //Filter menu items based on user role
  let filteredMenuItems = INSTRUCTOR_MENU_ITEMS;

  //load gradebook for the last selected group on initial load if on instructor page
  useEffect(() => {
    fetchData();
  }, [loggedInUser, selectedPage, location.pathname]);  //trigger when location.pathname changes (url navigation)

  //update the group list drop down after creating a new group in Groups Admin Page
  useEffect(() => {
    fetchGroups();
  }, [adminGroupList]);

  const fetchData = async () => {
    const groupsResult = await fetchGroups() as GroupType[];

    // If there are groups, select the last selected group or the first available group
    if (groupsResult && groupsResult.length > 0) {
      let targetGroupId = '';

      // Try to use last selected group first
      if (loggedInUser && loggedInUser.lastGroupSelectionId) {
        const lastSelectedGroup = groupsResult.find(group => group.id === loggedInUser.lastGroupSelectionId);
        if (lastSelectedGroup) {
          targetGroupId = lastSelectedGroup.id;
        }
      }

      // If no last selected group, use the first available group
      if (!targetGroupId && groupsResult.length > 0) {
        const firstValidGroup = groupsResult.find(group => group.activeSessionId || group.lastActiveSessionId);
        if (firstValidGroup) {
          targetGroupId = firstValidGroup.id;
        }
      }

      if (targetGroupId) {
        setSelectedCourseId(targetGroupId);
        const tempSelectedGroupData = groupsResult.find(group => group.id === targetGroupId) || null;
        setSelectedGroupData(tempSelectedGroupData);

        await fetchQuizzes(tempSelectedGroupData);

        // Navigate to the gradebook if we're on the instructor landing page
        if (location.pathname === '/instructor') {
          const group = groupsResult.find(group => group.id === targetGroupId);
          if (group) {
            const activeSessionId = group.activeSessionId || group.lastActiveSessionId;
            const finalPath = `/instructor/gradebook/${activeSessionId}`;
            navigate(finalPath, { replace: true });

            // Update user's last selected group if it's different
            if (loggedInUser?.id && targetGroupId !== loggedInUser.lastGroupSelectionId) {
              updateUserLastGroupSelectionId(loggedInUser.id, targetGroupId);
              if (loggedInUser) {
                loggedInUser.lastGroupSelectionId = targetGroupId;
              }
            }
          }
        }
      }
    }
  };

  const fetchGroups = useCallback(async () => {
    if (loggedInUser)  //if logged in user has been obtained
    {
      let result;
      try {
        if (loggedInUser.roles[0] === Roles.SystemAdmin) //If logged in user is system admin then return all groups
          result = await getGroups();
        else if (loggedInUser.roles[0] === Roles.OrganizationAdmin) //If organization admin return all groups in the admins organization
        {
          result = await getGroupsPerOrganization(loggedInUser.organizationId);
        }
        else if (loggedInUser.roles[0] === Roles.Instructor) //If instructor return all groups owned by instructor
        {
          //Get group per owner id
          result = await getGroups(loggedInUser.id);
        }

        setCurrentGroupList(result.data);  //Store the group data portion of the group list
        return result.data;  // Return the groups data

      } catch (error: any) {
        const errorMsg = `Failed to get groups: ${error.title}`;
        console.error(errorMsg);
        // props.onNotification(errorMsg, "error"); //TODO - add functionality to display error message
      }
    }
  }, [loggedInUser]);

  //Set selected path when menu item is chosen
  useEffect(() => {
    if (location.pathname.includes('/instructor/gradebook')) {
      const pathParts = location.pathname.split('/');
      //if pathParts 3 is not empty then replace the value with ":sessionId"
      setSelectedPath(pathParts[3] ? '/instructor/gradebook' : location.pathname);
      return;
    }

    setSelectedPath(location.pathname);
  }, [location]);

  const fetchQuizzes = useCallback(async (passedSelectedGroupData: GroupType | null) => {
    let quizScenarios: ScenarioHeader[] = [];
    let validUser = false;

    //If course has students then fetch quizzes
    if (passedSelectedGroupData && passedSelectedGroupData.memberIds.length > 0) {
      // If there are memberIds retrieve a valid user and fetch quiz otherwise do not fetch quizzes
      for (let i = 0; i < passedSelectedGroupData.memberIds.length; i++) {
        let studentId = passedSelectedGroupData.memberIds[i];

        let response;
        try {
          response = await getUser(studentId.replace(/"|'/g, ''));
        } catch (err) {
          let msg = "Failed to fetch user data";
          console.error(msg, err);
          // onNotification(msg, "error");  //TODO - add functionality to display error message
        }

        if (response?.data) { // If a valid user is found then break the loop
          validUser = true;
          break;
        }
      }
    }
    if (validUser) {
      try {
        const response = await getScenarioHeaders(COURSE_ID);
        // Filter for quiz type scenarios only
        quizScenarios = response.data.filter((scenario: ScenarioHeader) =>
          scenario.scenarioType === 'Quiz'
        );
      } catch (error) {
        console.error('Failed to fetch quizzes:', error);
      }
    }
    else
      console.log("No Students in Course")

    setQuizzes(quizScenarios);
  }, []);

  const handleCourseChange = async (courseId: string, path: string) => {
    setSelectedCourseId(courseId);
    //Save the selection to the user's last group selection
    try {
      if (loggedInUser?.id) {
        // Update the last selected group id for the user 
        updateUserLastGroupSelectionId(loggedInUser.id, courseId);
        // Update the last selected group id in the context
        loggedInUser.lastGroupSelectionId = courseId;
      } else {
        throw new Error("Logged in user ID is undefined");
      }
    }
    catch (error: any) {
      console.error(`Failed to update last group selection: ${error.message}`);
    }

    // set selectedGroupData to show the join id in the sidebar
    const tempSelectedGroupData = currentGroupList.find(group => group.id === courseId) || null;
    setSelectedGroupData(tempSelectedGroupData);

    // if Gradebook or quiz menu item is selected, navigate to the gradebook page automatically
    if (selectedPath.includes('/instructor/gradebook' )) {
      const activeSessionId = tempSelectedGroupData?.activeSessionId || tempSelectedGroupData?.lastActiveSessionId || '';
      const finalPath = '/instructor/gradebook' + '/' + activeSessionId;
      setSelectedPath(finalPath);
      navigate(finalPath, { replace: true });
      fetchQuizzes(tempSelectedGroupData);
      // clear the quiz context when reloading the gradebook page
      if (setQuizContext) {
        setQuizContext('', '');
      } else {
        console.error("setQuizContext is undefined");
      }
    }
  };

  //Handle Sidebar button click
  const handleSidebarButtonClick = (path: string) => {
    // clear the quiz context when making another menu selection
    if (setQuizContext) {
      setQuizContext('', '');
    } else {
      console.error("setQuizContext is undefined");
    }

    if (path === '/instructor/gradebook') {
      const tempSelectedGroupData = currentGroupList.find(group => group.id === selectedCourseId) || null;
      setSelectedGroupData(tempSelectedGroupData);

      const activeSessionId = tempSelectedGroupData?.activeSessionId || tempSelectedGroupData?.lastActiveSessionId || '';

      const finalPath = '/instructor/gradebook' + '/' + activeSessionId;
      setSelectedPath(finalPath);

      navigate(finalPath, { replace: true });
      
      fetchQuizzes(tempSelectedGroupData);
    }
    else {
      setSelectedPath(path);
      navigate(path, { replace: true });
    }
  }

  //#region 
  // JSX components
  interface CourseDropdownProps {
    groupList: GroupType[];
    selectedCourseId?: string;
    path: string;
    onCourseChange: (courseId: string, path: string) => void;
  }

  interface QuizButtonProps {
    isOpen: boolean;
    onQuizClick: (scenarioId: string, shortTitle: string) => void;
  }

  // QuizButton component
  const QuizButton: React.FC<QuizButtonProps> = ({ isOpen, onQuizClick }) => {
    // Find the selected quiz based on currentQuizId set from GradebookTable
    const selectedQuiz = quizzes.find(quiz => quiz.scenarioId === currentQuizId) || null; // If no quiz is selected, return null
    // if selectedQuiz is null, return null jsx element
    if (!selectedQuiz) {
      return (
          null
      );
  }

    // Use shortTitle if available, otherwise fall back to title
    const quizDisplayName = selectedQuiz.scenarioShortTitle || selectedQuiz.scenarioTitle;

    return (
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        {/* Quiz Button List */}
        <List disablePadding>
          {/* Quiz Button Button */}
            <ListItemButton
              key={selectedQuiz.scenarioId}
              onClick={() => onQuizClick(selectedQuiz.scenarioId, quizDisplayName)}
              sx={{
                borderRadius: '30px',
                margin: '4px',
                backgroundColor: decodeURIComponent(location.pathname).includes(quizDisplayName) ? colorAuriaYellow : 'rgba(0, 0, 0, 0.04)',
              }}
            >
              {/* Quiz Button Icon */}
              <ListItemIcon sx={{ color: decodeURIComponent(location.pathname).includes(quizDisplayName) ? 'black' : '' }}>
                {<Ballot />}
              </ListItemIcon>

              <ListItemText
                primary={quizDisplayName}
                primaryTypographyProps={{
                  sx: { color: decodeURIComponent(location.pathname).includes(quizDisplayName) ? 'black' : '' }
                }}
              />
            </ListItemButton>
        </List>
      </Collapse>
    );
  };

  const CourseDropdown: React.FC<CourseDropdownProps> = ({ groupList, selectedCourseId, path, onCourseChange }) => {
    return (
      <FormControl className="admin-sidebar-menu-items" sx={{
        display: 'flex',
        padding: '8px 16px',
        margin: '4px 4px',
        width: 'calc(100% - 8px)', // Stretch full width minus margins
      }}
        size="small">
        <Select
          labelId="course-select-label"
          id="course-select"
          value={selectedCourseId || ''}
          onChange={(event) => onCourseChange(event.target.value, path)}
          variant='standard'
          displayEmpty
        >
          <MenuItem value="" disabled>
            Select Course
          </MenuItem>
          {groupList
            .filter(group => group.activeSessionId || group.lastActiveSessionId) //Filter out groups that can have a gradebook
            .map(group => (
              <MenuItem key={group.id} value={group.id}>
                {group.name}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
    );
  };
  //#endregion

  const fetchSession = async (sessionId: string | null): Promise<GroupCompetition | undefined> => {
    if (!sessionId) return undefined;

    try {
      //Obtain Session Information
      const sessionResponse = await getSessionWithSessionId(sessionId);
      return sessionResponse.data as GroupCompetition;

    } catch (error) {
      let msg = "Failed to fetch session data";
      console.error(msg, error);
      // onNotification(msg, "error");  //TODO - add functionality to display error message
    }
  };

  const handleQuizClick = async (quizId: string, shortTitle: string) => {
    // No quiz click logic required at this time
  };

  return (
    <Box>
      {/* Main Navigation Menu */}
      <List className="admin-sidebar-menu-items">
        {filteredMenuItems.map((item) => (
          <ListItem key={item.path} disablePadding sx={{ flexDirection: 'column' }}>
            {/* Course Dropdown Menu Item */}
            {item.menuItemType === "dropDown" ? (
              <CourseDropdown
                groupList={currentGroupList}
                selectedCourseId={selectedCourseId}
                path={item.path}
                onCourseChange={handleCourseChange}
              />
            ) : (
              <>
                {/* Regular Menu Items (Gradebook, View All Courses, etc.) */}
                <ListItemButton
                  onClick={() => handleSidebarButtonClick(item.path)}
                  disabled={item.path === '/instructor/gradebook' && currentGroupList.length === 0} // Disable if gradebook button and no groups
                  sx={{
                    backgroundColor: selectedPath === item.path && !currentQuizId ? colorAuriaYellow : 'transparent',  // Highlight selected path but not if quiz is selected
                    borderRadius: '30px',
                    padding: '8px 16px',
                    margin: '4px 4px',
                    width: 'calc(100% - 8px)',
                  }}
                >
                  {/* Menu Item Icon */}
                  <ListItemIcon sx={{ color: selectedPath === item.path && !currentQuizId ? 'black' : '' }}>
                    {item.icon}
                  </ListItemIcon>

                  {/* Menu Item Text */}
                  <ListItemText
                    primary={item.title}
                    primaryTypographyProps={{
                      sx: { color: selectedPath === item.path && !currentQuizId ? 'black' : '' }
                    }}
                  />
                </ListItemButton>

                {/* Quiz Button  */}
                {item.path === '/instructor/gradebook' && (
                  <Box sx={{ width: '100%', pl: 2 }}>
                    <QuizButton
                      isOpen={true}
                      onQuizClick={handleQuizClick}
                    />
                  </Box>
                )}
              </>
            )}
          </ListItem>
        ))}
      </List>

      {/* Access Code Display Section */}
      {selectedGroupData && (
        <Box sx={{ padding: '16px' }}> {/* Outer Box with external padding */}
          <Box sx={{  // Inner colored box 
            padding: '8px', backgroundColor: '#e0e0e0', borderRadius: '8px',
            display: 'flex',
            justifyContent: 'center', // Center horizontally
            alignItems: 'center',    // Center vertically
          }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'black', textAlign: 'center' }}>
              Access Code: <br /> {selectedGroupData.password}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default InstructorSidebar;